// src/chatbot/components/VoiceRecordingCard.tsx
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { SendHorizontal, Pause, Play } from 'lucide-react';
import WaveSurfer from 'wavesurfer.js';

interface VoiceRecordingCardProps {
    isVisible: boolean;
    isRecording: boolean;
    recordingDuration: number;
    audioBlob: Blob | null;
    mediaStream: MediaStream | null;
    theme: any;
    onCancel: () => void;
    onSend: () => void;
    formatRecordingTime: (seconds: number) => string;
}

const VoiceRecordingCard: React.FC<VoiceRecordingCardProps> = ({
    isVisible,
    isRecording,
    recordingDuration,
    audioBlob,
    mediaStream,
    theme,
    onCancel,
    onSend,
    formatRecordingTime
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const waveformContainerRef = useRef<HTMLDivElement>(null);
    const waveSurferInstanceRef = useRef<WaveSurfer | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Live recording visualizer using analyser
    useEffect(() => {
        if (!isRecording || !mediaStream || !canvasRef.current) {
            const canvasElement = canvasRef.current;
            const context = canvasElement?.getContext('2d');
            if (context && canvasElement) {
                context.clearRect(0, 0, canvasElement.width, canvasElement.height);
            }
            return;
        }

        const typedWindow = window as typeof window & { webkitAudioContext?: typeof AudioContext };
        const AudioContextConstructor = window.AudioContext || typedWindow.webkitAudioContext;
        if (!AudioContextConstructor) {
            console.warn('AudioContext is not supported in this browser.');
            return;
        }

        const audioContext = new AudioContextConstructor();
        const source = audioContext.createMediaStreamSource(mediaStream);
        const analyser = audioContext.createAnalyser();
        source.connect(analyser);
        analyser.fftSize = 1024;

        const bufferLength = analyser.fftSize;
        const dataArray = new Uint8Array(bufferLength);
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) {
            audioContext.close();
            return;
        }

        let animationFrame = 0;

        const draw = () => {
            animationFrame = requestAnimationFrame(draw);
            analyser.getByteTimeDomainData(dataArray);
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.beginPath();
            const sliceWidth = canvas.width / bufferLength;
            let x = 0;

            for (let i = 0; i < bufferLength; i += 1) {
                const v = dataArray[i] / 128.0;
                const y = (v * canvas.height) / 2;

                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }

                x += sliceWidth;
            }

            ctx.lineWidth = 1.5;
            ctx.strokeStyle = theme.primaryColor;
            ctx.stroke();
        };

        draw();

        return () => {
            cancelAnimationFrame(animationFrame);
            audioContext.close();
        };
    }, [isRecording, mediaStream, theme.primaryColor]);

    // Recorded waveform using WaveSurfer
    useEffect(() => {
        if (!audioBlob || !waveformContainerRef.current) {
            waveSurferInstanceRef.current?.destroy();
            waveSurferInstanceRef.current = null;
            setIsPlaying(false);
            return;
        }

        const container = waveformContainerRef.current;
        container.innerHTML = '';

        const url = URL.createObjectURL(audioBlob);
        const waveSurfer = WaveSurfer.create({
            container,
            waveColor: `${theme.primaryColor}80`,
            progressColor: theme.primaryColor,
            cursorColor: theme.primaryColor,
            height: 50,
            normalize: true
        });

        waveSurferInstanceRef.current = waveSurfer;

        const handleFinish = () => setIsPlaying(false);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        waveSurfer.on('finish', handleFinish);
        waveSurfer.on('play', handlePlay);
        waveSurfer.on('pause', handlePause);

        waveSurfer.load(url);

        return () => {
            waveSurfer.un('finish', handleFinish);
            waveSurfer.un('play', handlePlay);
            waveSurfer.un('pause', handlePause);
            waveSurfer.destroy();
            waveSurferInstanceRef.current = null;
            URL.revokeObjectURL(url);
            setIsPlaying(false);
        };
    }, [audioBlob, theme.primaryColor]);

    const handleTogglePlayback = () => {
        const waveSurfer = waveSurferInstanceRef.current;
        if (!waveSurfer) return;
        waveSurfer.playPause();
        setIsPlaying(waveSurfer.isPlaying());
    };

    if (!isVisible) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="mb-3 p-3 rounded-lg relative z-10"
            style={{
                backgroundColor: `${theme.primaryColor}10`,
                border: `1px solid ${theme.primaryColor}30`,
                minHeight: '140px'
            }}
        >
            {/* Recording Header - Compact */}
            <div className="flex items-center justify-between ">
                <div className="flex items-center gap-2">
                    {isRecording && (
                        <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.6, repeat: Infinity }}
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: theme.primaryColor }}
                        />
                    )}
                    <span className="text-xs font-medium" style={{ color: theme.primaryColor }}>
                        {isRecording ? 'Recording' : 'Voice Message Ready'}
                    </span>
                </div>
                <div className="text-xs font-medium" style={{ color: theme.primaryColor }}>
                    {formatRecordingTime(recordingDuration)}
                </div>
            </div>

            {/* Audio Visualizer */}
            <div className="flex flex-col items-center justify-center gap-2 h-10  rounded overflow-hidden"
                style={{ backgroundColor: `${theme.primaryColor}05` }}>
                {isRecording ? (
                    <canvas
                        ref={canvasRef}
                        width={280}
                        height={60}
                        className="rounded"
                    />
                ) : audioBlob ? (
                    <div className="w-full">
                        <div ref={waveformContainerRef} className="w-full h-[50px]" />
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-1 h-6">
                        {Array.from({ length: 25 }, (_, i) => {
                            const height = Math.max(0.3, Math.sin(Date.now() * 0.01 + i * 0.5) * 0.5 + 0.5);
                            const delay = i * 0.05;
                            return (
                                <motion.div
                                    key={i}
                                    className="w-0.5 rounded-full"
                                    animate={{
                                        height: `${8 + height * 20}px`,
                                        opacity: 0.6 + height * 0.4
                                    }}
                                    style={{ backgroundColor: theme.primaryColor }}
                                    transition={{
                                        duration: 0.15,
                                        ease: "easeOut",
                                        delay: Math.sin(Date.now() * 0.01 + delay) * 0.1
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Recording Controls - Compact */}
            <div className="flex items-center justify-center gap-3 pt-1 pb-1 ">
                {audioBlob && (
                    <motion.button
                        onClick={handleTogglePlayback}
                        className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: `${theme.primaryColor}15`, color: theme.primaryColor }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={isPlaying ? 'Pause playback' : 'Play recording'}
                    >
                        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </motion.button>
                )}

                {/* Cancel Button */}
                <motion.button
                    onClick={onCancel}
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: `${theme.secondaryColor}20`, color: theme.secondaryColor }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Cancel recording"
                >
                    <FaTimes size={14} />
                </motion.button>

                {/* Send Button - Show during recording and after */}
                <motion.button
                    onClick={onSend}
                    className="px-4 py-2 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: theme.primaryColor, color: '#ffffff' }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title={isRecording ? "Stop and send recording" : "Send recording"}
                >
                    <SendHorizontal size={16} />
                </motion.button>
            </div>
        </motion.div>
    );
};

export default VoiceRecordingCard;